import { EventEmitter, Injectable } from '@angular/core';
import {HttpBaseService} from './http-base.service';
import {StorageService} from './storage.service';
import { Router } from '@angular/router';
import { removeSessionUser, setSessionUser } from '../../store/user/user.action';
import { removeReviewer, setReviewer } from '../../store/reviewer/reviewer.action';
import { Store } from '@ngrx/store';
import { FirebaseMessagingService } from './firebase-messaging.service';
import { environment } from '../../../environments/environment';
import { lastValueFrom } from 'rxjs';
import { UserService } from './user.service';
import { ConfigurationService } from './configuration.service';
import { constants } from '../constants/constants';
import { HttpParams } from '@angular/common/http';
import { ClientPortalService } from './client-portal.service';
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  eventEmitter: EventEmitter<any> = new EventEmitter();
  constructor(
    private http: HttpBaseService,
    private storageService: StorageService,
    private firebaseMessagingService: FirebaseMessagingService,
    private store: Store,
    private router: Router,
    private userService: UserService,
    private configurationService: ConfigurationService,
    private clientPortalService: ClientPortalService
  ) { }

  public login(payload:any) {
    let params = new HttpParams().set('noCache', new Date().getTime())
    return this.http.post('auth/login', payload, {params});
  }

  public signup(payload: any){
    return lastValueFrom(this.http.post('auth/signup', payload));
  }

  public refreshToken() {
    const payload = {
      refreshToken: this.storageService.getCookieValueByKey('refreshToken'),
      expiryFor: 'web',
    }
    return this.http.post('auth/refreshToken', payload);
  }

  public getSessionUser() {
    return this.http.get('my');
  }

  public removeAllExcept(keysToKeep: string[]): void{
    const itemsToKeep: { [key: string]: string } = {};
    
    keysToKeep.forEach(key => {
      const value = localStorage.getItem(key);
      if (value !== null) {
        itemsToKeep[key] = value;
      }
    });
  
    localStorage.clear();
  
    Object.keys(itemsToKeep).forEach(key => {
      localStorage.setItem(key, itemsToKeep[key]);
    });
  };
  

  public logout(param: boolean = false):void{
    this.firebaseMessagingService.logoutFromFirebase();
    this.storageService.removeAllCookies()
    this.store.dispatch(removeReviewer());
    this.store.dispatch(removeSessionUser());
    this.removeAllExcept(['isSideNavOpen']);
    if(!param){
      window.location.href = `${environment.clientHost}login`;
    }
  }

  public logoutWithoutNavigation():void{
    this.firebaseMessagingService.logoutFromFirebase();
    this.storageService.removeAllCookies()
    this.store.dispatch(removeReviewer());
    this.store.dispatch(removeSessionUser());
    this.removeAllExcept(['isSideNavOpen']);
  }

  async autoLogin(token: string, state: string, isMobile: boolean, firmId: string, mergeInvite: boolean) {    
    try {
      const UserObject = this.parseUserToken(token);
      if (UserObject.role === constants.userRoles.client) {
        await this.autoLoginClientPortalUser(UserObject);
        return;
      }
      await this.initializeUserSession(UserObject);
      const sessionUser = await this.fetchSessionUser(UserObject);
      
      if (sessionUser.message) {
        return;
      }

      this.setupUserSession(sessionUser, firmId);
      this.handleMobileNavigation(state, isMobile);
      this.handleExternalAuthNavigation(UserObject.externalAuth, sessionUser, firmId, mergeInvite);
    } catch (error) {
      this.logout();
    }
  }

  /**
   * MP-5750: SSO return path for a CLIENT. `my` and `configurations` answer
   * 403 for this role, so the session comes from the portal session endpoint
   * and the store gets the minimal user block it returns. The only landing
   * page is the Started project list.
   */
  private async autoLoginClientPortalUser(UserObject: any): Promise<void> {
    this.removeAllExcept(['isSideNavOpen']);
    this.storageService.setUserToken(UserObject);
    const session = await this.clientPortalService.loadSession();
    this.store.dispatch(setSessionUser({ ...session.user, role: constants.userRoles.client }));
    this.router.navigateByUrl(constants.clientPortal.homeUrl);
  }

  private parseUserToken(token: string): any {
    return JSON.parse(decodeURIComponent(token));
  }

  private async initializeUserSession(UserObject: any): Promise<void> {
    this.removeAllExcept(['isSideNavOpen']);
    this.storageService.setUserToken(UserObject);
    this.storageService.setCookieValue('firebaseToken', UserObject.firebaseToken);
    await this.configurationService.getConfiguration();
  }

  private async fetchSessionUser(UserObject: any): Promise<any> {
    if (UserObject.firmUser) {
      return await this.userService.getUserById(UserObject.firmUser);
    }
    return await lastValueFrom(this.http.get(`my`));
  }

  private setupUserSession(sessionUser: any, firmId: string): void {
    this.store.dispatch(setReviewer(sessionUser));
    this.store.dispatch(setSessionUser(sessionUser));
    
    const currentTenant = this.findCurrentTenant(sessionUser.tenants, firmId);
    const singleTenant = sessionUser.tenants.length ? sessionUser.tenants[0] : '';
    this.storageService.setTenant(currentTenant || singleTenant);
  }

  private findCurrentTenant(tenants: any[], firmId: string): any {
    return tenants.find((tenant: any) => tenant.firm._id === firmId);
  }

  private handleMobileNavigation(state: string, isMobile: boolean): void {
    if (state === 'my-profile' && isMobile) {
      this.router.navigateByUrl('/reviewer/profile/main');
    }
  }

  private handleExternalAuthNavigation(externalAuth: string, sessionUser: any, firmId: string, mergeInvite: boolean): void {
    if (externalAuth !== 'true') {
      return;
    }

    if (sessionUser.role === constants.userRoles.reviewer) {
      this.handleReviewerNavigation(sessionUser, firmId, mergeInvite);
    } else {
      this.router.navigateByUrl('/client');
    }
  }

  private handleReviewerNavigation(sessionUser: any, firmId: string, mergeInvite: boolean): void {
    const incompleteTenant = this.findIncompleteTenant(sessionUser.tenants);
    const hasVerifiedTenant = this.hasVerifiedTenant(sessionUser.tenants);
    const isPasswordChanged = sessionUser.lastPasswordChangedAt !== null;

    if (hasVerifiedTenant || isPasswordChanged) {
      this.redirectToLogin(firmId);
    } else if (incompleteTenant) {
      this.redirectToOnboarding(incompleteTenant, mergeInvite);
    } else {
      this.router.navigateByUrl('/reviewer');
    }
  }

  private findIncompleteTenant(tenants: any[]): any {
    return tenants.find((t: any) => t.reviewerStatus === constants.userVerificationStatus.incomplete);
  }

  private hasVerifiedTenant(tenants: any[]): boolean {
    return tenants.findIndex((e: any) => 
      e.reviewerStatus === constants.userVerificationStatus.verified || 
      e.reviewerStatus === constants.userVerificationStatus.pendingAdminVerification
    ) > -1;
  }

  private redirectToLogin(firmId: string): void {
    this.logoutWithoutNavigation();
    this.router.navigateByUrl(`/login?firm=${firmId}`);
  }

  private redirectToOnboarding(tenant: any, mergeInvite: boolean): void {
    const firmId = tenant.firm._id ? tenant.firm._id.toString() : tenant.firm;
    const mergeInviteParam = mergeInvite ? `&mergeInvite=${mergeInvite}` : '';
    this.router.navigateByUrl(`/onboarding?firm=${firmId}${mergeInviteParam}`);
  }

  public changePassword (oldPassword: string, newPassword: string, confirmPassword: string) {
    let refreshObject = {
      refreshToken: this.storageService.getCookieValueByKey('refreshToken'),
      expiryFor: 'web',
    };
    let pass: any = {
      oldPassword,
      newPassword,
      confirmPassword,
      refreshObject
    };
    return this.http.put('auth/changePassword', pass);
  }

  forgotPassword(email: {email: string}) {
    return this.http.put('auth/forgotPassword', email);
  }

  resetPassword(password: any){
      return this.http.put('auth/resetPassword', password);
  }

  getUserByHash (hash:string) {
    return this.http.get(`auth/getUserByRecoveryHash/${hash}`);
  };

  getNotInterestedUserByHash (hash:string) {
    return lastValueFrom(this.http.get(`auth/getNotInterestedUserByRecoveryHash/${hash}`));
  };

  updateUser(user: any) {
    return this.http.put('auth/user/update', user);
  };

  sendAdminVerificationPhoneSms(adminId: string, mobileNo: string) {
    const body = {
      _id: adminId,
      ...(mobileNo ? { mobile: mobileNo } : {})
    }
    return this.http.put('auth/sendVerificationPhoneSms', body);
  }
  
  verifyPhone(body: any ) {
    return this.http.put('auth/verifyPhone', body);
  }

  isAuthenticated (){
    return !!(this.storageService.getToken());
  }
  preLoginVerification(email:string){
    return this.http.post('auth/pre-login-verification',{email});
  }
  offerRejectByEmail (hash:string) {
    return lastValueFrom(this.http.put(`offers/rejectByEmail`, { token: hash }));
  };


}
